// Chat in the dashboard: the signed-in customer's own conversation, every
// channel in one thread, through this app's Worker (the session cookie never
// reaches page JS), via /v1/me/conversation (draft.13). The assistant's
// buttons and list rows are tappable and go back as replies. A photo or PDF
// is uploaded as a document, then sent into the chat with any text as its
// caption; the assistant reads it as it would a WhatsApp photo.
import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as D from '@radix-ui/react-dialog';
import { FileText, Loader2, MessageCircle, Paperclip, Send, X } from 'lucide-react';
import { ApiError, documentUrl, ok, uploadDocument } from '@cover/core/api/client.ts';
import type { Message } from '@cover/core/api/types.ts';
import { BrandMark, useBrand, whatsappLink } from '@cover/core/brand.tsx';
import { cn } from '@cover/core/cn.ts';
import { channelLabel, displayPhone, firstName, time } from '@cover/core/format.ts';
import { useToast } from '@cover/core/ui/Toast.tsx';
import { ChatText } from '@cover/core/ui/ChatText.tsx';
import { Thumb } from '@cover/core/ui/Thumb.tsx';
import { useSession } from '../session.tsx';
import { api } from '../api.ts';

type Chat = { open: (text?: string) => void };
const ChatContext = createContext<Chat | null>(null);

export function useChat() {
  const c = useContext(ChatContext);
  if (!c) throw new Error('useChat outside ChatProvider');
  return c;
}

export const chatKey = ['my-chat'] as const;

export function ChatProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<{ open: boolean; draft: string }>({ open: false, draft: '' });
  return (
    <ChatContext.Provider value={{ open: (text) => setState({ open: true, draft: text ?? '' }) }}>
      {children}
      {state.open && <ChatDrawer draft={state.draft} onClose={() => setState({ open: false, draft: '' })} />}
    </ChatContext.Provider>
  );
}

function useThread(enabled: boolean, waiting: boolean) {
  const qc = useQueryClient();
  return useQuery({
    enabled,
    queryKey: chatKey,
    queryFn: async () => {
      const prev = qc.getQueryData<Message[]>(chatKey);
      const after = prev?.at(-1)?.id;
      const page = await ok(api.GET('/me/conversation/messages', { params: { query: { after, limit: 200 } } }));
      return after ? [...(prev ?? []), ...page.data] : page.data;
    },
    // Every second while a reply is due, otherwise every three.
    refetchInterval: waiting ? 1000 : 3000,
    retry: false,
  });
}

/** Append messages the thread doesn't have yet, keeping it in order. */
function addToThread(qc: ReturnType<typeof useQueryClient>, m: Message) {
  qc.setQueryData<Message[]>(chatKey, (prev = []) => (prev.some((x) => x.id === m.id) ? prev : [...prev, m]));
}

type Outgoing = { id: string; text: string; failed?: boolean };

function ChatDrawer({ draft, onClose }: { draft: string; onClose: () => void }) {
  const brand = useBrand();
  const { me } = useSession();
  const toast = useToast();
  const qc = useQueryClient();
  // When we last sent something; the assistant is "typing" until a reply arrives after it (or a minute passes).
  const [sentAt, setSentAt] = useState<string | null>(null);
  const [now, setNow] = useState(Date.now());
  const thread = useThread(true, !!sentAt);
  const replied = !!sentAt && !!thread.data?.some((m) => m.direction === 'out' && m.createdAt >= sentAt);
  const waiting = !!sentAt && !replied && now - Date.parse(sentAt) < 60_000;
  useEffect(() => {
    if (!sentAt) return;
    if (replied) return setSentAt(null);
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [sentAt, replied]);
  const [text, setText] = useState(draft);
  const [outgoing, setOutgoing] = useState<Outgoing[]>([]);
  const end = useRef<HTMLDivElement>(null);
  const count = (thread.data?.length ?? 0) + outgoing.length + (waiting ? 1 : 0);
  useEffect(() => {
    end.current?.scrollIntoView({ block: 'end' });
  }, [count]);

  // An API without the chat (an older engine): offer WhatsApp instead.
  const unavailable = thread.error instanceof ApiError && (thread.error.status === 404 || thread.error.status === 405);
  const phone = displayPhone(me.contact?.phoneMasked);
  const wa = phone ? whatsappLink(brand, text || undefined) : null;

  const { businessId } = useSession();
  const fileInput = useRef<HTMLInputElement>(null);
  const tooMany = (e: unknown) => (e instanceof ApiError && e.status === 429 ? new Error('That’s a lot of messages in a short time. Give it a minute, or message us on WhatsApp.') : e);
  // The API answers as soon as it has stored the message; the assistant's reply follows on the thread.
  const post = async (body: { text?: string; reply?: { id: string; title: string }; documentId?: string }) => {
    const m = await ok(api.POST('/me/conversation/messages', { body }));
    addToThread(qc, m);
    setSentAt(m.createdAt);
    return m;
  };
  /** Show it straight away (dimmed), clear the box, and swap in the stored message when it's back. */
  const sendText = async (t: string, retryOf?: string) => {
    const id = retryOf ?? `out-${crypto.randomUUID()}`;
    setOutgoing((o) => (retryOf ? o.map((x) => (x.id === id ? { ...x, failed: false } : x)) : [...o, { id, text: t }]));
    try {
      await post({ text: t });
      setOutgoing((o) => o.filter((x) => x.id !== id));
    } catch (e) {
      setOutgoing((o) => o.map((x) => (x.id === id ? { ...x, failed: true } : x)));
      toast.fail(tooMany(e));
    }
  };
  const submit = () => {
    const t = text.trim();
    if (!t) return;
    setText('');
    void sendText(t);
  };
  const reply = useMutation({ mutationFn: (r: { id: string; title: string }) => post({ reply: r }), onError: (e) => toast.fail(tooMany(e)) });
  // Upload first (it needs their business), then send it in; whatever they'd typed becomes the caption.
  const attach = useMutation({
    mutationFn: async (file: File) => {
      if (file.size > 16 * 1024 * 1024) throw new Error('That file is over 16 MB. Try a smaller photo or PDF.');
      const doc = await uploadDocument(businessId, file, { source: 'dashboard' });
      const caption = text.trim();
      await post({ documentId: doc.id, ...(caption ? { text: caption } : {}) });
      if (caption) setText('');
    },
    onError: (e) => toast.fail(e),
    onSettled: () => {
      if (fileInput.current) fileInput.current.value = '';
    },
  });
  // Only the latest assistant message's buttons are live; older ones are history.
  const lastOut = [...(thread.data ?? [])].reverse().find((m) => m.direction === 'out');

  return (
    <D.Root open onOpenChange={(o) => !o && onClose()}>
      <D.Portal>
        <D.Overlay className="fixed inset-0 z-40 bg-black/30" />
        <D.Content
          aria-describedby={undefined}
          className="fixed inset-0 z-50 flex flex-col bg-canvas focus:outline-none sm:inset-y-3 sm:left-auto sm:right-3 sm:w-[420px] sm:overflow-hidden sm:rounded-card sm:shadow-pop sm:ring-1 sm:ring-line"
        >
          <header className="flex items-center gap-3 border-b border-line bg-white px-4 py-3 pt-[max(0.75rem,env(safe-area-inset-top))]">
            <BrandMark />
            <div className="min-w-0 flex-1">
              <D.Title className="sr-only">Chat with {brand.name}</D.Title>
            </div>
            <D.Close className="flex h-11 w-11 items-center justify-center rounded-control text-mid hover:bg-surface" aria-label="Close chat">
              <X className="h-5 w-5" aria-hidden />
            </D.Close>
          </header>

          <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-4" aria-live="polite" aria-relevant="additions">
            {thread.isLoading ? (
              <div className="flex justify-center py-10 text-mid">
                <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
                <span className="sr-only">Loading</span>
              </div>
            ) : unavailable ? (
              <div className="rounded-card bg-white p-4 text-sm ring-1 ring-line">
                <p className="font-semibold">Chat here is nearly ready.</p>
                <p className="mt-1 text-mid">{wa ? 'For now, message us on WhatsApp and we’ll pick it up from there.' : `For now, call us${brand.contact.phone ? ` on ${brand.contact.phone}` : ''} or use the chat on our website.`}</p>
              </div>
            ) : (
              <>
                {!count && (
                  <div className="rounded-card bg-white p-4 text-sm ring-1 ring-line">
                    <p className="font-semibold">Hi{firstName(me.contact?.displayName) ? ` ${firstName(me.contact?.displayName)}` : ''} 👋</p>
                    <p className="mt-1 text-mid">Ask anything about your cover, or tell us about your work. Our assistant answers here, and a person can step in whenever you’d like.</p>
                  </div>
                )}
                {thread.data?.map((m) => (
                  <Bubble
                    key={m.id}
                    m={m}
                    onReply={m === lastOut && !reply.isPending && !waiting ? (r) => reply.mutate(r) : undefined}
                    onImage={() => end.current?.scrollIntoView({ block: 'end' })}
                  />
                ))}
                {outgoing.map((o) => (
                  <div key={o.id} className="flex flex-col items-end">
                    <div className={cn('max-w-[85%] whitespace-pre-line rounded-card rounded-br-sm bg-brand px-3.5 py-2.5 text-[15px] leading-relaxed text-on-brand', !o.failed && 'opacity-60')}>{o.text}</div>
                    <p className="mt-1 px-1 text-xs text-muted">
                      {o.failed ? (
                        <>
                          <span className="text-bad-fg">Not sent</span> ·{' '}
                          <button type="button" className="font-medium text-ink underline" onClick={() => void sendText(o.text, o.id)}>
                            Try again
                          </button>
                        </>
                      ) : (
                        'Sending…'
                      )}
                    </p>
                  </div>
                ))}
                {waiting && (
                  <div className="flex items-start" role="status">
                    <div className="flex items-center gap-1 rounded-card rounded-bl-sm bg-white px-4 py-3.5 ring-1 ring-inset ring-line">
                      <span className="sr-only">{brand.name} is replying</span>
                      {[0, 150, 300].map((d) => (
                        <span key={d} className="h-2 w-2 animate-bounce rounded-full bg-muted" style={{ animationDelay: `${d}ms` }} aria-hidden />
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
            <div ref={end} />
          </div>

          <form
            className="border-t border-line bg-white p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]"
            onSubmit={(e) => {
              e.preventDefault();
              submit();
            }}
          >
            {!unavailable && (
              <div className="flex items-end gap-2 rounded-control bg-white p-1.5 ring-1 ring-inset ring-line-strong focus-within:ring-2 focus-within:ring-brand">
                {businessId && (
                  <>
                    <input ref={fileInput} type="file" accept="image/*,application/pdf,.heic" className="sr-only" tabIndex={-1} aria-hidden onChange={(e) => e.target.files?.[0] && attach.mutate(e.target.files[0])} />
                    <button
                      type="button"
                      onClick={() => fileInput.current?.click()}
                      disabled={attach.isPending}
                      className="flex h-11 w-11 shrink-0 items-center justify-center rounded-control text-mid hover:bg-surface hover:text-ink disabled:opacity-50"
                      aria-label="Attach a photo or PDF"
                    >
                      {attach.isPending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Paperclip className="h-4 w-4" aria-hidden />}
                    </button>
                  </>
                )}
                <label htmlFor="chat-text" className="sr-only">
                  Message
                </label>
                <textarea
                  id="chat-text"
                  autoFocus
                  rows={1}
                  maxLength={4096}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      submit();
                    }
                  }}
                  placeholder="Write a message"
                  className="max-h-40 min-h-11 flex-1 resize-none bg-transparent px-2 py-2.5 text-[15px] outline-none placeholder:text-muted"
                />
                <button type="submit" disabled={!text.trim()} className="flex h-11 w-11 shrink-0 items-center justify-center rounded-control bg-brand text-on-brand disabled:opacity-50" aria-label="Send">
                  <Send className="h-4 w-4" aria-hidden />
                </button>
              </div>
            )}
            {wa && (
              <a href={wa} className={cn('flex min-h-11 items-center justify-center gap-2 text-sm font-medium text-mid no-underline hover:text-ink hover:no-underline', !unavailable && 'mt-1')}>
                <MessageCircle className="h-4 w-4" aria-hidden /> {unavailable ? 'Message us on WhatsApp' : 'Prefer WhatsApp?'}
              </a>
            )}
          </form>
        </D.Content>
      </D.Portal>
    </D.Root>
  );
}

type Reply = { id: string; title: string };

/** Tappable choices on an assistant message: buttons, or a list's rows. */
function choices(m: Message): Reply[] {
  if (m.content.type === 'buttons') return m.content.buttons.map((b) => ({ id: b.id, title: b.title }));
  if (m.content.type === 'list') return m.content.sections.flatMap((sec) => sec.rows.map((r) => ({ id: r.id, title: r.title })));
  return [];
}

function Bubble({ m, onReply, onImage }: { m: Message; onReply?: (r: Reply) => void; onImage?: () => void }) {
  const mine = m.direction === 'in';
  const who = mine ? null : m.author === 'agent' ? 'Our team' : m.author === 'bot' ? 'Assistant' : null;
  const c = m.content;
  const text =
    c.type === 'text' || c.type === 'buttons' || c.type === 'list' || c.type === 'cta_url'
      ? c.text
      : c.type === 'reply'
        ? c.title
        : c.type === 'voice'
          ? (c.transcript ?? 'A voice note')
          : c.type === 'media'
            ? (c.caption ?? '')
            : m.text;
  const options = mine ? [] : choices(m);
  return (
    <div className={cn('flex flex-col', mine ? 'items-end' : 'items-start')}>
      {c.type === 'media' && c.documentId && (
        <a href={documentUrl(c.documentId)} target="_blank" rel="noopener" className="mb-1 block max-w-[85%] no-underline hover:no-underline">
          {c.mediaType === 'image' ? (
            <Thumb documentId={c.documentId} alt={c.caption ?? 'Photo'} loading="eager" onLoad={onImage} className="max-h-56 rounded-card object-cover ring-1 ring-line" fallback={<FileChip name={c.filename ?? 'Photo'} />} />
          ) : (
            <FileChip name={c.filename ?? 'Document'} />
          )}
        </a>
      )}
      {(c.type !== 'media' || !!c.caption) && (
        <div className={cn('max-w-[85%] whitespace-pre-line rounded-card px-3.5 py-2.5 text-[15px] leading-relaxed', mine ? 'rounded-br-sm bg-brand text-on-brand' : 'rounded-bl-sm bg-white text-ink ring-1 ring-inset ring-line')}>
          {text ? <ChatText text={text} /> : <span className="italic opacity-80">A message</span>}
          {c.type === 'cta_url' && (
            <a href={c.url} target="_blank" rel="noopener" className="mt-2 block font-semibold">
              {c.label}
            </a>
          )}
        </div>
      )}
      {options.length > 0 && (
        <div className="mt-2 flex max-w-[85%] flex-wrap gap-1.5" role="group" aria-label="Choose a reply">
          {options.map((o) => (
            <button
              key={o.id}
              type="button"
              disabled={!onReply}
              onClick={() => onReply?.(o)}
              className="min-h-10 rounded-full bg-white px-3.5 text-sm font-medium text-brand ring-1 ring-inset ring-brand/40 hover:bg-brand/5 disabled:text-muted disabled:ring-line"
            >
              {o.title}
            </button>
          ))}
        </div>
      )}
      <p className="mt-1 px-1 text-xs text-muted">
        {who && `${who} · `}
        {m.channel && m.channel !== 'web' && `via ${m.channel === 'email' ? 'email' : channelLabel(m.channel)} · `}
        {time(m.createdAt)}
      </p>
    </div>
  );
}

function FileChip({ name }: { name: string }) {
  return (
    <span className="inline-flex max-w-full items-center gap-2 rounded-card bg-white px-3 py-2.5 text-sm font-medium text-ink ring-1 ring-inset ring-line">
      <FileText className="h-4 w-4 shrink-0 text-mid" aria-hidden />
      <span className="truncate">{name}</span>
    </span>
  );
}
